import React, { useState, useEffect, useRef } from "react";
import {
  Shield,
  Activity,
  Users,
  TrendingUp,
  Eye,
  Calendar,
  Globe,
  Monitor,
  ChevronLeft,
  ChevronRight,
  Database,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { MetricCard, MetricCardGrid } from "../../widgets/MetricCard";
import Loader from "../../widgets/Loader";
import AuditTrailFilters from "../../components/auditTrail/AuditTrailFilters";
import AuditTrailDetailModal from "../../components/auditTrail/AuditTrailDetailModal";
import auditTrailService, {
  AuditTrailReport,
  AuditTrailListParams,
  AuditTrailStats,
  EventType,
} from "../../services/auditTrail.service";
import Tooltip from "../../widgets/ToolTip";

const AuditTrailReports: React.FC = () => {
  // State management
  const [reports, setReports] = useState<AuditTrailReport[]>([]);
  // const [isLoading] = useState(false);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [filters, setFilters] = useState<AuditTrailListParams>({
    skip: 0,
    limit: 10,
  });
  const [stats, setStats] = useState<AuditTrailStats>({
    total_events: 0,
    today_events: 0,
    unique_users: 0,
    most_common_event: "N/A",
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<AuditTrailReport | null>(
    null
  );

  const isInitialMount = useRef(true);

  // Fetch data on mount and filter changes
  useEffect(() => {
    if (isInitialMount.current) {
      fetchReports();
      fetchStats();
      fetchEventTypes();
      isInitialMount.current = false;
    } else {
      const debounceTimer = setTimeout(() => {
        fetchReports();
      }, 500);
      return () => clearTimeout(debounceTimer);
    }
  }, [filters]);

  // Fetch reports function
  const fetchReports = async () => {
    try {
      setIsLoadingReports(true);
      const response = await auditTrailService.getAuditTrailReports(filters);
      setReports(response.data.audit_trail_reports);
      setTotalCount(response.data.total_count);
    } catch (error) {
      console.error("Error fetching audit trail reports:", error);
      toast.error("Failed to load audit trail reports");
    } finally {
      setIsLoadingReports(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      setIsLoadingStats(true);
      const reportStats = await auditTrailService.getAuditTrailStats();
      setStats(reportStats);
    } catch (error) {
      console.error("Error fetching audit trail stats:", error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Fetch event types
  const fetchEventTypes = async () => {
    try {
      const types = await auditTrailService.getEventTypes();
      setEventTypes(types);
    } catch (error) {
      console.error("Error fetching event types:", error);
    }
  };

  // Handle filters change
  const handleFiltersChange = (newFilters: AuditTrailListParams) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  // Handle view details
  const handleViewDetails = (report: AuditTrailReport) => {
    setSelectedReport(report);
    setShowDetailModal(true);
  };

  // Close detail modal
  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setSelectedReport(null);
  };

  // Export data
  // const handleExport = (format: "csv" | "json") => {
  //   if (reports.length === 0) {
  //     toast.error("No data to export");
  //     return;
  //   }
  //   try {
  //     auditTrailService.exportAuditTrailData(reports, format);
  //     toast.success(`Data exported as ${format.toUpperCase()}`);
  //   } catch (error) {
  //     console.error("Error exporting data:", error);
  //     toast.error("Failed to export data");
  //   }
  // };

  // Pagination handler
  const handlePageChange = (page: number) => {
    const newFilters = {
      ...filters,
      skip: (page - 1) * pageSize,
    };
    setFilters(newFilters);
    setCurrentPage(page);
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Get event type color
  const getEventTypeColor = (eventType: string) => {
    const colors = {
      LOGIN: "bg-green-100 text-green-800",
      LOGOUT: "bg-gray-100 text-gray-800",
      SETTINGS_UPDATED: "bg-blue-100 text-blue-800",
      USER_CREATED: "bg-purple-100 text-purple-800",
      USER_UPDATED: "bg-yellow-100 text-yellow-800",
      FILE_UPLOADED: "bg-indigo-100 text-indigo-800",
      FILE_DOWNLOADED: "bg-cyan-100 text-cyan-800",
      DOCUMENT_VIEWED: "bg-orange-100 text-orange-800",
      ERROR: "bg-red-100 text-red-800",
      WARNING: "bg-amber-100 text-amber-800",
    };
    return (
      colors[eventType as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Audit Trail Reports
                </h1>
                <p className="text-xs text-slate-600">
                  Monitor and analyze system activities and user actions
                </p>
              </div>
              {/* <div className="flex gap-3">
            <button
              onClick={() => handleExport("csv")}
              disabled={isLoading || reports.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport("json")}
              disabled={isLoading || reports.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-4 h-4" />
              Export JSON
            </button>
          </div> */}
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <MetricCardGrid>
          <MetricCard
            title="Total Events"
            value={isLoadingStats ? "..." : stats.total_events}
            icon={Database}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
          />
          <MetricCard
            title="Today's Events"
            value={isLoadingStats ? "..." : stats.today_events}
            icon={Activity}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
          />
          <MetricCard
            title="Unique Users"
            value={isLoadingStats ? "..." : stats.unique_users}
            icon={Users}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
          />
          <MetricCard
            title="Top Event"
            value={isLoadingStats ? "..." : stats.most_common_event}
            icon={TrendingUp}
            iconColor="text-orange-600"
            iconBgColor="bg-orange-100"
          />
        </MetricCardGrid>

        {/* Filters */}
        <AuditTrailFilters
          onFiltersChange={handleFiltersChange}
          eventTypes={eventTypes}
          isLoading={isLoadingReports}
          onRefresh={fetchReports}
        />

        {/* Reports Table */}
        {isLoadingReports ? (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader />
          </div>
        ) : reports.length > 0 ? (
          <>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Device
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reports.map((report) => (
                      <tr key={report._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mr-3">
                              <Shield className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getEventTypeColor(
                                    report.event_type
                                  )}`}
                                >
                                  {report.event_type.replace(/_/g, " ")}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 truncate max-w-xs">
                                {report.event_description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {report.user_info?.email || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500 font-mono">
                            {report.user_id
                              ? `${report.user_id.substring(0, 8)}...`
                              : "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            {formatDate(report.event_time).toString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Globe className="w-4 h-4 mr-2 text-gray-400" />
                            {report.ip_address}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Monitor className="w-4 h-4 mr-2 text-gray-400" />
                            <div>
                              <div>
                                {report.device_info?.browser?.name ||
                                  "Unknown Browser"}
                              </div>
                              <div className="text-xs text-gray-500">
                                {report.device_info?.os?.name || "Unknown OS"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(report)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          >
                            <Tooltip content="View">
                              <Eye className="w-4 h-4" />
                            </Tooltip>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{startItem}</span> to{" "}
                <span className="font-medium">{endItem}</span> of{" "}
                <span className="font-medium">{totalCount}</span> events
              </div>
              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No audit trail events found
            </h3>
            <p className="text-gray-500 mb-6">
              No events match your current search criteria. Try adjusting your
              filters.
            </p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AuditTrailDetailModal
        isOpen={showDetailModal}
        onClose={handleCloseDetail}
        report={selectedReport}
      />
    </div>
  );
};

export default AuditTrailReports;
